<template>
  <div class="filter">
    <component
      class="filter__item"
      v-for="key in components"
      :key="key"
      :options="options[key]"
      :is="getType(key)"
      :value="get(key)"
      :data-filter="key"
      @input="$ev => set(key, $ev)"
    >
      <template v-for="(index, name) in $slots" v-slot:[name]>
        <slot :name="name" />
      </template>
      <template v-for="(index, name) in $scopedSlots" v-slot:[name]="data">
        <slot :name="name" v-bind="data"></slot>
      </template>
    </component>

    <Add :items="items" :options="options" :filter="value" @add="add" />
    <ButtonComponent
      v-if="isActive"
      :label="$t('reset')"
      class="filter__reset"
      :icon="['fal', 'times']"
      @click="onReset"
    />
  </div>
</template>

<script>
import StorageMixin from '@/mixins/Storage'
import { mergeAllDeepRight, Diff, hasDiff } from '@/utils'

import Widgets from './Widgets'
import Add from './Add'
import { ButtonComponent } from 'vue-elder-button'

export default {
  mixins: [
    StorageMixin({
      target: 'persistedFilter',
      key: 'filter',
      storage: 'routerStorage',
      strategy: 'merge',
      disabled() {
        return !this.persist
      },
    }),
  ],
  props: {
    value: Object,
    default: Object,
    items: {
      type: Array,
      default: () => ['search'],
    },
    persist: {
      type: Boolean,
      default: true,
    },
    options: {
      type: Object,
      default: () => ({}),
    },
  },
  watch: {
    defaultComp: {
      handler: 'onInput',
      immediate: true,
    },
  },
  computed: {
    defaultComp() {
      if (this.default) return this.default
      let routeDefaultFilter = this.$path('$route.meta.defaultFilter')
      if (!routeDefaultFilter) return {}

      return typeof routeDefaultFilter === 'function' ? routeDefaultFilter.call(this, this) : routeDefaultFilter
    },
    isActive() {
      return hasDiff(this.value, this.defaultComp)
    },
    components() {
      return Object.keys(this.value).filter(key => this.getType(key))
    },
    persistedFilter: {
      get() {
        return Diff(this.defaultComp, this.value)
      },
      set(val) {
        this.onInput(val)
      },
    },
  },
  methods: {
    get(key) {
      const transform = this.$path(`options.${key}.transformFilter.get`)
      if (transform) return transform(this.value)
      return this.value[key]
    },
    set(key, value) {
      let { alias } = this.parse(key)
      let data = { [alias]: value }

      const transform = this.$path(`options.${key}.transformFilter.set`)
      if (transform) data = transform(value, this.value)
      this.onInput(data)
    },
    add(key) {
      let { alias } = this.parse(key)
      this.set(alias, null)
    },
    getType(key) {
      let target = this.items.find(i => i === key || i.endsWith(':' + key))
      if (!target) return

      let [component] = target.split(':')
      return Widgets[component.toTitleCase()]
    },
    parse(key) {
      let [component, alias] = key.split(':')
      if (!alias) alias = component

      return { component, alias }
    },
    onInput(data = {}) {
      this.$emit('input', mergeAllDeepRight(this.defaultComp, this.value, data))
    },
    onReset() {
      if ('reset' in this.$listeners) return this.$emit('reset')
      return this.$emit('input', this.defaultComp)
    },
  },
  components: {
    Add,
    ButtonComponent,
    ...Widgets,
  },
}
</script>

<style lang="scss">
.filter {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: end;

  .elder-input {
    min-width: 8rem;
  }

  .elder-input__element {
    padding: 0.75rem;
  }

  &__reset {
    border-color: transparent;
    color: var(--error);
  }
}
</style>
